const SNAPSHOT_VERSION = 1;

export function newTargetTraineeRowKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `tt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Deep-clone a cohort template and assign fresh rowKey on each target trainee location.
 * @param {object} cohortTemplate e.g. trainingCohortDefaults
 */
export function cloneCohortWithNewTargetRowKeys(cohortTemplate) {
  const cohort = JSON.parse(JSON.stringify(cohortTemplate));
  (cohort.targetTrainees || []).forEach((tt) => {
    tt.rowKey = newTargetTraineeRowKey();
  });
  return cohort;
}

/**
 * @param {object} params
 * @returns {object} JSON-serializable draft payload (stored in DB)
 */
export function serializeTrainingFormDraft({
  title,
  providerProgram,
  subProgram,
  selectedTopics,
  needsAssessment,
  trainingCohorts,
  trainingMaterials,
}) {
  const toISO = (d) => {
    if (d == null) return null;
    if (d instanceof Date) return d.toISOString();
    if (typeof d === "string") return d;
    return null;
  };

  return {
    v: SNAPSHOT_VERSION,
    title: title || "",
    providerProgram: providerProgram || null,
    subProgram: subProgram || null,
    selectedTopics: selectedTopics || [],
    needsAssessmentDescription: needsAssessment?.description || "",
    trainingMaterials: (trainingMaterials || []).map((m) => ({
      id: m.id,
      description: m.description || "",
      url: m.url || "",
      errors: {},
    })),
    trainingCohorts: (trainingCohorts || []).map((cohort) => ({
      startDate: toISO(cohort.startDate),
      endDate: toISO(cohort.endDate),
      numOfTrainees: cohort.numOfTrainees ?? 0,
      numOfHours: cohort.numOfHours ?? 0,
      budget: cohort.budget ?? 0,
      errors: {},
      venues: (cohort.venues || []).map((v) => ({
        venueType: v.venueType || null,
        district: v.district || null,
        sectors: v.sectors || [],
        sector: v.sector || null,
        venues: v.venues || [],
        venue: v.venue || null,
        numOfTrainees: v.numOfTrainees ?? 0,
        trainerSearchTerm: v.trainerSearchTerm || "",
        trainers: (v.trainers || []).map((t) => ({ ...t })),
        errors: {},
      })),
      targetTrainees: (cohort.targetTrainees || []).map((tt) => ({
        rowKey: tt.rowKey || newTargetTraineeRowKey(),
        district: tt.district || null,
        sectors: tt.sectors || [],
        sector: tt.sector || null,
        schools: tt.schools || [],
        school: tt.school || null,
        errors: {},
        traineeCategories: (tt.traineeCategories || []).map((tc) => ({
          category: tc.category || null,
          subCategories: tc.subCategories || [],
          subCategory: tc.subCategory || null,
          subLevel: tc.subLevel || null,
          subjects: tc.subjects || [],
          subject: tc.subject || null,
          numOfTrainees: tc.numOfTrainees ?? "",
          errors: {},
        })),
      })),
    })),
  };
}

function parseDate(s) {
  if (s == null || s === "") return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {object} snapshot
 * @param {object} trainingCohortDefaults
 * @param {object} trainingMaterialDefaults
 * @returns {object|null}
 */
export function hydrateTrainingFormDraft(
  snapshot,
  trainingCohortDefaults,
  trainingMaterialDefaults,
) {
  if (!snapshot || snapshot.v !== SNAPSHOT_VERSION) {
    return null;
  }

  const defaultVenue = { ...trainingCohortDefaults.venues[0] };
  const defaultLocation = { ...trainingCohortDefaults.targetTrainees[0] };
  const defaultCategory = {
    ...trainingCohortDefaults.targetTrainees[0].traineeCategories[0],
  };

  const mapCohort = (c) => ({
    ...trainingCohortDefaults,
    startDate: parseDate(c.startDate),
    endDate: parseDate(c.endDate),
    numOfTrainees: c.numOfTrainees ?? 0,
    numOfHours: c.numOfHours ?? 0,
    budget: c.budget ?? 0,
    errors: {},
    venues:
      c.venues?.length > 0
        ? c.venues.map((v) => ({
            ...defaultVenue,
            ...v,
            trainers: (v.trainers || []).map((t) => ({ ...t })),
            errors: {},
          }))
        : [{ ...defaultVenue }],
    targetTrainees:
      c.targetTrainees?.length > 0
        ? c.targetTrainees.map((tt) => ({
            ...defaultLocation,
            ...tt,
            rowKey: tt.rowKey || newTargetTraineeRowKey(),
            errors: {},
            traineeCategories:
              tt.traineeCategories?.length > 0
                ? tt.traineeCategories.map((tc) => ({
                    ...defaultCategory,
                    ...tc,
                    errors: {},
                  }))
                : [{ ...defaultCategory }],
          }))
        : [
            {
              ...defaultLocation,
              rowKey: newTargetTraineeRowKey(),
              errors: {},
              traineeCategories:
                (defaultLocation.traineeCategories || []).length > 0
                  ? (defaultLocation.traineeCategories || []).map((tc) => ({
                      ...defaultCategory,
                      ...tc,
                      errors: {},
                    }))
                  : [{ ...defaultCategory, errors: {} }],
            },
          ],
  });

  return {
    title: snapshot.title || "",
    providerProgram: snapshot.providerProgram || null,
    subProgram: snapshot.subProgram || null,
    selectedTopics: snapshot.selectedTopics || [],
    needsAssessment: {
      file: null,
      description: snapshot.needsAssessmentDescription || "",
    },
    trainingMaterials:
      snapshot.trainingMaterials?.length > 0
        ? snapshot.trainingMaterials.map((m) => ({
            ...trainingMaterialDefaults,
            ...m,
            errors: {},
          }))
        : [{ ...trainingMaterialDefaults }],
    trainingCohorts:
      snapshot.trainingCohorts?.length > 0
        ? snapshot.trainingCohorts.map(mapCohort)
        : [cloneCohortWithNewTargetRowKeys(trainingCohortDefaults)],
  };
}
